// Earth — warm-dark "bolder earthy". Flat-solid espresso surfaces, warm
// off-white text, and a spread of earthy accents: terracotta (primary),
// ochre (warning), olive (success), clay (deep). No frosted glass — the
// material is flat (blur off, no bevel highlights, no elevation
// shadows); cards separate by fill step + slightly stronger borders.

import type { ThemeDefinition } from "./types";
import { FONTS, makeTypography } from "./shared";

// Flat themes need a no-op (but valid) box-shadow value: `none` would
// invalidate the comma-joined box-shadow lists the presets build, so
// use a transparent zero-size shadow instead.
const NO_SHADOW = "0 0 0 0 transparent";

export const earth: ThemeDefinition = {
  name: "earth",
  label: "Earth",
  description: "Warm espresso surfaces with terracotta accents. Solid, no blur.",
  mode: "dark",
  browserChrome: "#1a1916",

  // Warm editorial: an elegant serif for display titles, a humanist
  // grotesk for body, and quiet sentence-case labels (rendered as typed —
  // label text is authored in sentence case, never title-cased by CSS).
  typography: makeTypography({
    fonts: { sans: FONTS.hanken, display: FONTS.fraunces },
    label: { case: "none", tracking: "0.01em", font: "sans", weight: 600 },
    // Tracking sits at 0, not negative: Fraunces is already generously
    // proportioned, and pulling it tighter is what made titles read
    // compressed. Weight 500 gives the softer strokes presence at
    // display sizes without tipping into bold.
    display: { weight: 500, letterSpacing: "0" },
    // a link is marked by more than its hue (quiet underline, index.css)
    linkUnderline: true,
  }),
  // Generous, soft corners.
  radius: { sm: "3px", md: "5px", lg: "6px", xl: "6px", xxl: "8px" },

  color: {
    ink: "232, 228, 216", // warm parchment instead of pure white
    inkSpace: "232 228 216",
    accentRgb: "217, 119, 87", // terracotta

    bg: "#1a1916",
    surface0: "#161512",
    // Warm espresso steps: the panel sits one visible step above the
    // page (the old #1f1f1e was a neutral grey at the page's brightness,
    // so cards separated by hue alone).
    surface1: "#24211d",
    surface2: "#2e2b26",
    surface3: "#38352f",

    textPrimary: "#f3f0e8",
    textMuted: "rgba(243, 240, 232, 0.74)",
    // faint/ghost clear AA (>= 4.5:1) on bg, panel and raised.
    textFaint: "rgba(243, 240, 232, 0.58)",
    textGhost: "rgba(243, 240, 232, 0.58)",

    // Darker-fill strategy: `accent` is a deeper terracotta FILL so white
    // text on primary buttons clears AA (5.0:1; the old #d97757 was 3.1),
    // hover goes DARKER still (accent-600, 5.9:1). `accentLight` is the
    // accent as text / links / active tab. `accentRgb` (tints, halos,
    // selection) stays the vivid terracotta.
    accent: "#b0553a",
    accentLight: "#f0ad93",
    accentDark: "#a04c32",
    accentDeeper: "#8a4b34",
    onAccent: "#ffffff",

    success: "#9aab6e", // olive
    warning: "#e0a948", // ochre
    // Danger is a cool crimson, well away from the terracotta accent
    // (dE ~21 fill to fill, ~23 as text; the old red-clay #b5463d was ~10
    // from the accent fill, so "Delete" looked like "Save"). AA as text
    // on raised-over-tile (4.9:1) and on its /15 tint; white on the
    // fill is 7.5:1.
    danger: "#ef8f9c",
    // Info is a dusty blue: the old sand #d7b98e was ochre's hue at
    // lower saturation, so info and warning read as one colour.
    info: "#a9bfd0",
    // Live work is verdigris (copper's patina, beside the terracotta):
    // the light terracotta sat dE ~15 from the danger pink, so a Running
    // badge read like a Failed one; this is ~53 from it and >= 20 from
    // olive, ochre and the dusty blue, 5.2:1 as text on raised.
    live: "#5fbfae",
    dangerFill: "#9b2c3a",
    focus: "#e89274",

    // flat-solid: opaque warm fills
    raised: "rgba(232, 228, 216, 0.08)",
    glassPanel: "#24211d",
    glassTile: "#2e2b26",
    glassKpi: "#292622",
    glassInset: "#141310",
    glassButton: "rgba(232, 228, 216, 0.05)",
    glassButtonHover: "rgba(232, 228, 216, 0.1)",

    activeAccent: "rgba(217, 119, 87, 0.14)",
    activeAccentBorder: "rgba(217, 119, 87, 0.34)",
    activeAccentStrong: "rgba(217, 119, 87, 0.44)",
    // web only: input/secondary-button edge, selected-row fill
    controlEdge: "rgba(232, 228, 216, 0.22)",
    selected: "rgba(217, 119, 87, 0.22)",

    avatarBg: "rgba(217, 119, 87, 0.2)",
    avatarBorder: "rgba(217, 119, 87, 0.28)",
    avatarText: "#f0cdbd",
    sidebarBg: "rgba(0, 0, 0, 0.16)",
    sidebarEdge: "rgba(232, 228, 216, 0.05)",
    brandBadge: "#8a4b34", // warm clay instead of steel-navy
    brandBadgeMuted: "#2a2620",
    canvasGrid: "#2a2823",

    selectionBg: "rgba(217, 119, 87, 0.3)",
    selectionText: "#f8f5ef",
    scrim: "rgba(0, 0, 0, 0.6)",
    modalPanel: "rgba(43, 40, 35, 0.96)",
    overlay: "#2b2823",
    tabbarBg: "#211f1b",
  },

  contrast: {
    // Flat: edges come from slightly stronger borders + the fill step,
    // not bevels or shadows. Borders bumped a touch so cards stay crisp.
    border: {
      hairline: "rgba(232, 228, 216, 0.11)",
      subtle: "rgba(232, 228, 216, 0.14)",
      visible: "rgba(232, 228, 216, 0.18)",
      strong: "rgba(232, 228, 216, 0.25)",
    },
    bevel: {
      // no top-edge "light catching glass" highlight — the main 3D tell
      top: "transparent",
      topBright: "transparent",
      topStrong: "transparent",
      // gentle recession for sunken inputs only
      bottom: "rgba(0, 0, 0, 0.18)",
      bottomDark: "rgba(0, 0, 0, 0.22)",
    },
  },

  material: {
    flat: true,
    blur: { lg: "none", md: "none", sm: "none" },
    shadow: {
      panel: NO_SHADOW,
      tile: NO_SHADOW,
      subtle: NO_SHADOW,
      sidebarEdge: "1px 0 0 rgba(232, 228, 216, 0.07)",
      sidebarCast: NO_SHADOW,
      accentHalo: NO_SHADOW,
      accentHaloStrong: NO_SHADOW,
    },
  },

  ambient: [
    "radial-gradient(ellipse 700px 460px at 82% 8%, rgba(217, 119, 87, 0.07), transparent)",
    "radial-gradient(ellipse 560px 520px at 16% 88%, rgba(143, 120, 80, 0.06), transparent)",
    "radial-gradient(ellipse 900px 320px at 50% 36%, rgba(224, 169, 72, 0.03), transparent)",
    "linear-gradient(165deg, #1f1e1b 0%, #1a1916 45%, #161512 100%)",
  ].join(", "),

  scales: {
    surface: {
      "50": "250 249 245",
      "100": "242 239 231",
      "200": "227 223 211",
      "300": "179 174 159",
      // 400-600 are TEXT greys (meta, placeholders): lifted to clear AA.
      "400": "172 166 150",
      "500": "166 159 142",
      // 600 aliases 500 (see glass.ts).
      "600": "166 159 142",
      "700": "52 50 43",
      "800": "39 37 32",
      "880": "31 29 25",
      "900": "26 25 22",
      "950": "19 18 16",
    },
    accent: {
      "50": "246 231 223",
      "100": "240 211 198",
      "200": "230 178 156",
      "300": "224 146 117",
      "400": "222 139 110",
      // 300/400 stay light (accent as text); 500 = the fill (#b0553a);
      // 600 = the DARKER hover fill; 700+ deeper still.
      "500": "176 85 58",
      "600": "160 76 50",
      "700": "140 67 44",
      "800": "124 58 38",
      "900": "94 44 29",
    },
  },

  extended: {
    accentSoftRgb: "224, 140, 108",
    accentLightRgb: "232, 146, 116",
    accentInfo2Rgb: "231, 181, 154",
    accentSkyRgb: "231, 168, 130",
    accentGlowRgb: "233, 165, 130",
    accentGlowDeepRgb: "201, 112, 72",
    researchRgb: "192, 116, 132", // warm dusty-plum instead of violet
    catTealRgb: "151, 168, 102", // olive
    accentInfo: "#eea886",
    accentInfo2: "#e7b59a",
    catViolet: "#c193a5", // warm mauve
    catSteel: "#c7a877", // warm tan
    // cool-1 / cw-slate(-2) are used as TEXT: AA on bg, panel, tile and
    // raised (over the tile too). Ladder stays ordered dim -> bright.
    cool1: "#a69f93",
    cool2: "#aca597",
    cool3: "#b3ab9d",
    cool4: "#bdb3a4",
    cwSlate: "#a9a395",
    cwSlate2: "#a7a093",
    cwBlue: "#d3a87c",
    cwBlueL: "#e1c69e",
    cwBlueXl: "#e6d5ba",
    cwViolet: "#cdab93",
    cwVioletL: "#dcb89f",
    cwVioletD: "#bd9478",
    orgnodeRgb: "40, 38, 33",
    orgnodeSelRgb: "64, 50, 40",
    coolShadowRgb: "0, 0, 0",
    coolOverlayRgb: "22, 19, 16",
  },

  js: {
    canvasGrid: "#2a2823",
    canvasMask: "rgba(22, 21, 18, 0.8)",
    edgeStroke: "#e89274",
    edgeRgb: "217, 119, 87",
    teamColours: [
      "rgba(217, 119, 87, 0.6)",
      "rgba(185, 132, 154, 0.6)",
      "rgba(151, 168, 102, 0.6)",
    ],
    avatarHues: [16, 30, 42, 54, 78, 92, 8, 36],
    avatarBg: [32, 22],
    avatarFg: [48, 72],
  },
};
